import { hasGlobal } from '@crown/framework/ir';
import { init as initNavbar } from './components/Navbar/index';
import { init as initSidebar } from './components/Sidebar/index';
import type { HTMLSignalElement } from './components/types';
import { effect } from './signal/effect';

type SidebarElement = HTMLSignalElement<{ visible: boolean; mini: boolean }>;

/** Below this the sidebar covers the page, so it starts closed and gets out of the way. */
const NARROW = '(max-width: 767px)';

const started = new WeakSet<Element>();

/**
 * Starts the application chrome a page shell writes in its HTML: the navbar,
 * the sidebar, and the navbar's toggle for the sidebar.
 *
 * Synchronous: when it returns every part has run its `init`, so the sidebar's
 * signals (`element.signal.visible`) are there to read. Components the
 * framework renders start themselves; only markup that arrives as HTML needs
 * this. Starting the same element twice is a no-op, and the returned function
 * stops everything this call started.
 *
 * On a narrow window the sidebar is a drawer over the page: closed to begin
 * with, inert while closed, and closed again by its close button, by Escape and
 * by following one of its links.
 */
export function startChrome(root: ParentNode = document): () => void {
  const stops: (() => void)[] = [];
  const start = (element: HTMLElement, run: () => () => void) => {
    if (started.has(element)) return false;
    started.add(element);
    stops.push(effect(run), () => started.delete(element));
    return true;
  };

  root.querySelectorAll<HTMLElement>('[data-dls-component="navbar"]').forEach((navbar) => {
    start(navbar, () => initNavbar(navbar));
  });

  // Every sidebar is started; the toggle drives the first one this call started.
  let sidebar: SidebarElement | undefined;
  for (const candidate of Array.from(root.querySelectorAll<SidebarElement>('[data-dls-component="sidebar"]'))) {
    if (start(candidate, () => initSidebar(candidate))) sidebar ??= candidate;
  }

  const toggle = root.querySelector<HTMLButtonElement>('.navbar-toggle');
  const visible = sidebar?.signal?.visible;
  if (sidebar && toggle && visible) stops.push(wireToggle(sidebar, toggle, visible));

  return () => {
    for (const stop of stops.splice(0).reverse()) stop();
  };
}

function wireToggle(
  sidebar: SidebarElement,
  toggle: HTMLButtonElement,
  visible: NonNullable<SidebarElement['signal']>['visible'],
): () => void {
  const narrow = hasGlobal('matchMedia') ? window.matchMedia(NARROW) : null;
  const isNarrow = () => narrow?.matches ?? false;
  const close = (refocus: boolean) => {
    visible.set(false);
    if (refocus) toggle.focus();
  };

  const listeners: [EventTarget, string, EventListener][] = [
    [toggle, 'click', () => visible.set(!visible.get())],
    [sidebar, 'click', (event) => {
      if (!(event.target instanceof Element)) return;
      if (event.target.closest('[data-close-navigation]')) close(true);
      else if (isNarrow() && event.target.closest('a[href]')) close(false);
    }],
    [sidebar, 'keydown', (event) => {
      if (isNarrow() && event instanceof KeyboardEvent && event.key === 'Escape') close(true);
    }],
  ];
  const follow = () => visible.set(!isNarrow());
  if (narrow) listeners.push([narrow, 'change', follow]);

  follow();
  for (const [target, type, listener] of listeners) target.addEventListener(type, listener);
  const stopEffect = effect(() => {
    const open = visible.get();
    toggle.setAttribute('aria-expanded', String(open));
    sidebar.inert = isNarrow() && !open;
  });

  return () => {
    stopEffect();
    for (const [target, type, listener] of listeners) target.removeEventListener(type, listener);
  };
}
